import { useState } from "react";
import { Loader2 } from "lucide-react";
import { guardarContexto } from "../datos/marcaApi";
import type { MarcaDS } from "../datos/marcaApi";
import { useEscrituraVigente } from "../datos/useEscrituraVigente";

/**
 * Tab Perfil de la pantalla Marca: nombre, rubro y contexto.
 *
 * Solo el CONTEXTO es editable acá (`brand_profiles.context`, el mismo campo
 * que la "Memoria de la IA" de "Mi marca" → "Mi diseño"). El nombre, el rubro
 * y el logo quedan en lectura a propósito: en `config_sections/CompanyData.tsx`
 * esos tres, además de escribir `brand_profiles`, ESPEJAN a `company_profiles`
 * cuando la marca es la principal (y el rubro pasa además por
 * `mergeLastCrearAnuncioState`). Replicar ese efecto cruzado desde una segunda
 * pantalla es la forma más rápida de desincronizar las dos, así que se editan
 * en un solo lugar.
 */

interface MarcaPerfilPanelProps {
  marca: MarcaDS;
  /**
   * El contexto se escribió de verdad en `brand_profiles`. Se avisa aunque el
   * panel ya no esté vigente: la base cambió y el resto de la app tiene que
   * releer, se haya ido el usuario de la pantalla o no.
   */
  onMarcaEscrita: () => void;
  /** Guardado OK: el mensaje lo muestra `MarcaPage`. */
  onGuardado: (texto: string) => void;
  onError: (texto: string) => void;
}

const claseEtiquetaCampo = "text-[11px] font-medium uppercase tracking-wide text-[#02062B]/[0.5]";
const claseCard = "rounded-2xl border border-[#02062B]/[0.08] bg-white p-4";
const claseBotonSecundario =
  "rounded-full border border-[#02062B]/[0.12] py-2 px-4 text-[12px] font-medium text-brand-black transition-colors hover:border-[#02062B]/[0.25] disabled:opacity-50";
const claseBotonPrimario =
  "inline-flex items-center gap-1.5 rounded-full bg-brand-primary py-2 px-4 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50";

export const MarcaPerfilPanel = ({
  marca,
  onMarcaEscrita,
  onGuardado,
  onError,
}: MarcaPerfilPanelProps) => {
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState("");
  const [guardando, setGuardando] = useState(false);
  const sigueVigente = useEscrituraVigente(marca.id);

  const contextoGuardado = marca.context ?? "";
  const hayCambios = borrador !== contextoGuardado;

  const guardar = async () => {
    // Doble freno al doble click: `disabled` en el botón + este chequeo, por si
    // un segundo click entra en el mismo tick.
    if (guardando || !hayCambios) return;
    // El id se captura ANTES del await: si el usuario cambia de marca mientras
    // la escritura está en vuelo, `sigueVigente` corta y no se aplica nada
    // sobre la marca nueva.
    const idAlDisparar = marca.id;
    setGuardando(true);
    // Sin `trim()`: `CompanyData.tsx` guarda el textarea tal cual, y acá tiene
    // que quedar exactamente el mismo texto.
    const resultado = await guardarContexto(idAlDisparar, borrador);
    // Antes del chequeo de vigencia a propósito: si se escribió, la base cambió
    // y hay que avisarlo aunque el usuario ya no esté en esta marca.
    if (resultado.ok) onMarcaEscrita();
    if (!sigueVigente(idAlDisparar)) return;
    setGuardando(false);
    if (!resultado.ok) {
      onError(resultado.error ?? "No se pudo guardar el contexto.");
      return;
    }
    setEditando(false);
    onGuardado("Listo: guardamos el contexto de tu marca.");
  };

  return (
    <div className="max-w-2xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className={claseCard}>
          <p className={claseEtiquetaCampo}>Nombre</p>
          <p className="mt-1.5 text-[15px] font-semibold text-brand-black">
            {marca.name || "Sin nombre todavía"}
          </p>
        </div>
        <div className={claseCard}>
          <p className={claseEtiquetaCampo}>Rubro</p>
          <p className="mt-1.5 text-[15px] font-semibold text-brand-black">
            {marca.rubro || "Sin definir todavía"}
          </p>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-[#02062B]/[0.4]">
        El nombre y el rubro se editan en Configuración → Mis marcas.
      </p>

      <div className={`mt-4 ${claseCard}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className={claseEtiquetaCampo}>Contexto</p>
          {editando ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditando(false)}
                disabled={guardando}
                className={claseBotonSecundario}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardar}
                disabled={guardando || !hayCambios}
                className={claseBotonPrimario}
              >
                {guardando && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {guardando ? "Guardando…" : "Guardar contexto"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setBorrador(contextoGuardado);
                setEditando(true);
              }}
              className={claseBotonSecundario}
            >
              Editar
            </button>
          )}
        </div>

        {editando ? (
          <textarea
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            disabled={guardando}
            rows={10}
            placeholder="Anotá tono, público, producto estrella, lo que hay que evitar y cualquier detalle que Atlas deba recordar."
            className="mt-3 min-h-[180px] w-full resize-y rounded-xl border border-[#02062B]/[0.12] py-2.5 px-3 text-[13px] leading-relaxed text-brand-black outline-none transition-colors focus:border-brand-primary disabled:opacity-60"
          />
        ) : marca.context ? (
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] leading-relaxed text-[#02062B]/[0.75]">
            {marca.context}
          </p>
        ) : (
          <p className="mt-1.5 text-[13px] text-[#02062B]/[0.5]">
            Todavía no cargaste contexto para esta marca.
          </p>
        )}
      </div>
    </div>
  );
};

export default MarcaPerfilPanel;
